// src/components/admin/AdminRecipeModal.tsx
import React, { useEffect, useState } from 'react'
import fetchWithAuth from '../../utils/fetchWithAuth'

type Ingredient = {
  quantity: string
  unit: string
  catalog: { nameCanonical: string }
}

type RecipeDetail = {
  id: string
  title: string
  description?: string
  ingredients: Ingredient[]
  instructions: string[]
}

interface Props {
  recipeId: string | null
  isOpen: boolean
  onClose: () => void
}

export default function AdminRecipeModal({
  recipeId,
  isOpen,
  onClose,
}: Props) {
  const [recipe, setRecipe] = useState<RecipeDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen || !recipeId) return

    // reset state
    setError('')
    setRecipe(null)
    setLoading(true)

    fetchWithAuth(`/api/recipes/${recipeId}`)
      .then((data) => {
        setRecipe(data as RecipeDetail)
      })
      .catch(() => {
        setError('Could not load recipe.')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [isOpen, recipeId])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white max-w-lg w-full p-6 rounded shadow-lg relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
        >
          ✕
        </button>

        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="text-red-600">{error}</p>
        ) : recipe ? (
          <>
            {/* Recipe ID */}
            <p className="text-sm text-gray-500 mb-2">ID: {recipe.id}</p>

            {/* Title & Description */}
            <h2 className="text-xl font-bold mb-2">{recipe.title}</h2>
            {recipe.description && (
              <p className="text-gray-700 mb-4">{recipe.description}</p>
            )}

            {/* Ingredients */}
            <h3 className="font-semibold mb-1">Ingredients</h3>
            <ul className="list-disc list-inside mb-4">
              {recipe.ingredients.map((ing, i) => (
                <li key={i}>
                  {ing.quantity} {ing.unit} – {ing.catalog.nameCanonical}
                </li>
              ))}
            </ul>

            {/* Instructions */}
            <h3 className="font-semibold mb-1">Instructions</h3>
            <ol className="list-decimal list-inside">
              {recipe.instructions.map((step, i) => (
                <li key={i} className="mb-2">
                  {step}
                </li>
              ))}
            </ol>
          </>
        ) : null}
      </div>
    </div>
  )
}
